import { dataLabelingLabelStatusSchema } from 'pages/evaluations/metadata/labeling/utils/dataLabelingLabelStatusSchema'
import { documentsSearchSchema } from 'routes/documents/-searchParams'
import { z } from 'zod'

export const dataLabelingSessionSchema = z.enum([
  'unlabeled',
  'labeled_by_me',
  'deferred_by_me',
  'all_labeled',
  'all_deferred',
  'labeled_by_naes',
])

export const dataLabelingLabeledBySchema = z.enum(['everyone', 'me', 'naes'])

export type DataLabelingSession = z.infer<typeof dataLabelingSessionSchema>
export type DataLabelingLabelStatus = z.infer<
  typeof dataLabelingLabelStatusSchema
>
export type DataLabelingLabeledBy = z.infer<typeof dataLabelingLabeledBySchema>

export function isReviewerDataLabelingSession(
  session: DataLabelingSession | undefined
): boolean {
  return (
    session === 'all_labeled' ||
    session === 'all_deferred' ||
    session === 'labeled_by_naes'
  )
}

export function labeledByFromSession(
  session: DataLabelingSession
): DataLabelingLabeledBy {
  if (session === 'labeled_by_naes') return 'naes'
  if (session === 'all_labeled' || session === 'all_deferred') return 'everyone'
  return 'me'
}

export function isBrowseDataLabelingSession(
  session: DataLabelingSession | undefined
): boolean {
  return (
    session === 'labeled_by_me' ||
    session === 'deferred_by_me' ||
    session === 'all_labeled' ||
    session === 'all_deferred' ||
    session === 'labeled_by_naes'
  )
}

export const dataLabelingSearchSchema = documentsSearchSchema.extend({
  session: dataLabelingSessionSchema.optional(),
  browseSessionId: z.uuid().optional(),
  pageClassifications: z.array(z.string()).optional(),
  issuers: z.array(z.string()).optional(),
  labelStatus: dataLabelingLabelStatusSchema.optional(),
})

export type DataLabelingSearchParams = z.infer<typeof dataLabelingSearchSchema>

export const DATA_LABELING_LANDING_SEARCH: DataLabelingSearchParams = {
  session: undefined,
  browseSessionId: undefined,
  pageClassifications: undefined,
  issuers: undefined,
  labelStatus: undefined,
  searchQuery: undefined,
  searchInputQuery: undefined,
  searchFilterTitle: undefined,
  searchFilterDocumentTitle: undefined,
  searchFilterSystem: undefined,
  searchFilterDrawing: undefined,
  searchFilterVendor: undefined,
  searchFilterDocType: undefined,
  searchTab: undefined,
  searchBrowseAll: undefined,
  documentID: undefined,
  pageNumber: undefined,
  selectedBoxes: undefined,
  selectedRegions: undefined,
}

export function dataLabelingLandingSearch(
  previous: DataLabelingSearchParams
): DataLabelingSearchParams {
  return {
    ...DATA_LABELING_LANDING_SEARCH,
    pageClassifications: previous.pageClassifications,
    issuers: previous.issuers,
  }
}

export function dataLabelingSessionSearch(
  session: DataLabelingSession,
  previous: DataLabelingSearchParams
): DataLabelingSearchParams {
  return {
    ...previous,
    session,
    browseSessionId: session === 'unlabeled' ? undefined : crypto.randomUUID(),
    labelStatus: session === 'unlabeled' ? undefined : previous.labelStatus,
  }
}

export function dataLabelingTypeSearch(
  pageClassifications: string[] | undefined,
  previous: DataLabelingSearchParams
): DataLabelingSearchParams {
  return {
    ...previous,
    browseSessionId: isBrowseDataLabelingSession(previous.session)
      ? crypto.randomUUID()
      : undefined,
    pageClassifications,
  }
}

export function dataLabelingIssuersSearch(
  issuers: string[] | undefined,
  previous: DataLabelingSearchParams
): DataLabelingSearchParams {
  return {
    ...previous,
    browseSessionId: isBrowseDataLabelingSession(previous.session)
      ? crypto.randomUUID()
      : undefined,
    issuers,
  }
}

export function dataLabelingStatusSearch(
  labelStatus: DataLabelingLabelStatus | undefined,
  previous: DataLabelingSearchParams
): DataLabelingSearchParams {
  return {
    ...previous,
    browseSessionId: isBrowseDataLabelingSession(previous.session)
      ? crypto.randomUUID()
      : undefined,
    labelStatus,
  }
}

const SESSION_FOR_LABELED_BY: Record<
  DataLabelingLabeledBy,
  DataLabelingSession
> = {
  me: 'labeled_by_me',
  everyone: 'all_labeled',
  naes: 'labeled_by_naes',
}

export function dataLabelingLabeledBySearch(
  labeledBy: DataLabelingLabeledBy,
  previous: DataLabelingSearchParams
): DataLabelingSearchParams {
  return {
    ...previous,
    session: SESSION_FOR_LABELED_BY[labeledBy],
    browseSessionId: crypto.randomUUID(),
  }
}
